import { OdProtosCommonMarket_Market } from '__generated__/globalTypes';

export const LARGE_NUMBER_ROUND_TO = 5000;
const CLOSING_COSTS_CASH_RATE = 0.01;
const NUMBER_OF_MONTHS = 12.0;

export const DEFAULT_MORTGAGE_APR = 4;
export const DEFAULT_DOWN_PAYMENT_PERCENTAGE = 20;
export const DEFAUT_HOME_OWNERS_INSURANCE_PERCENTAGE = 0.22;
export const DEFAULT_CLOSING_COSTS_RATE = 0.0175;
export const MINIMUM_PURCHASE_PRICE_RATE = 0.5;
export const MAXIMUM_PURCHASE_PRICE_RATE = 1.5;

export const PRICE_ESTIMATOR_ANIMATION_DURATIONS = {
  MONTHLY_EXPENSES: 150,
  DUE_AT_CLOSE: 150,
};

/**
 * Helper function to calculate home insurance rates for BwOD markets
 */
export const getHomeownersInsurancePercentageForMarket = (
  market?: OdProtosCommonMarket_Market | null,
): number => {
  switch (market) {
    case OdProtosCommonMarket_Market.DALLAS:
    case OdProtosCommonMarket_Market.SAN_ANTONIO:
      return 1.09;
    case OdProtosCommonMarket_Market.PHOENIX:
      return 0.32;
    case OdProtosCommonMarket_Market.RALEIGH:
      return 0.52;
    case OdProtosCommonMarket_Market.ATLANTA:
      return 0.58;
    case OdProtosCommonMarket_Market.ORLANDO:
    case OdProtosCommonMarket_Market.TAMPA:
      return 1.17;
    case OdProtosCommonMarket_Market.LOS_ANGELES:
    case OdProtosCommonMarket_Market.RIVERSIDE:
      return 0.22;
    default:
      return DEFAUT_HOME_OWNERS_INSURANCE_PERCENTAGE;
  }
};

export const NUMBER_ANIMATION_INTERVAL = 16;
export const NUMBER_ANIMATION_STEPS = 30;

export enum MortgagePeriod {
  ThirtyYearFixed = '30-year fixed',
  FifteenYearFixed = '15-year fixed',
  SevenOneArm = '7/1 ARM',
  FiveOneArm = '5/1 ARM',
}

/**
 * Is this necessary?
 */
export const translateMortgagePeriodToLoanType = (
  mortgagePeriod: MortgagePeriod,
): string | undefined => {
  switch (mortgagePeriod) {
    case MortgagePeriod.ThirtyYearFixed:
      return '30_year_fixed';
    case MortgagePeriod.FifteenYearFixed:
      return '15_year_fixed';
    case MortgagePeriod.SevenOneArm:
      return '7_1_arm';
    case MortgagePeriod.FiveOneArm:
      return '5_1_arm';
    default:
      break;
  }
};

/**
 * Helper function to traslate loan type to mortgage period
 */
export const translateLoanTypeToMortgagePeriod = (loanType: string): MortgagePeriod => {
  switch (loanType) {
    case '30_year_fixed':
      return MortgagePeriod.ThirtyYearFixed;
    case '15_year_fixed':
      return MortgagePeriod.FifteenYearFixed;
    case '7_1_arm':
      return MortgagePeriod.SevenOneArm;
    case '5_1_arm':
      return MortgagePeriod.FiveOneArm;
    default:
      return MortgagePeriod.ThirtyYearFixed;
  }
};

// Values that the user can tweak
export type CostEstimatorInputParameters = {
  /**
   * Price in dollars (e.g 120000 for "$120,000")
   * @type {number}
   */
  purchasePrice: number;
  /**
   * Percentage (e.g. 20 for "20%")
   * @type {(number | null)}
   */
  downPaymentPercentage: number | null;
  downPaymentAmount: number | null;
  /**
   * Interest rate (e.g. 0.04 for "4%")
   * @type {number}
   */
  interestRate: number;
  mortgagePeriod: MortgagePeriod;
  userIsAlsoSelling: boolean;
};

// Values that come from the property/region etc...
// These cannot be changed by the user
export type CostEstimatorPropertyParameters = {
  /**
   * Insurance rate (e.g. 0.04 for "4%")
   * @type {number}
   */
  homeOwnersInsuranceRate: number;
  /**
   * Property tax rate (e.g. 0.04 for "4%")
   * @type {number}
   */
  propertyTaxRate: number;
  /**
   * Monthly fee in dollars (e.g. 30 for "$30")
   * @type {number}
   */
  monthlyHoaFee: number;
  /**
   * Closing costs rate (e.g. 0.04 for "4%")
   * @type {number}
   */
  closingCostsRate: number;
  // Commission offered by the seller in dollars
  commissionDollars?: number;
  // Commission offered by the seller in percent
  commissionPercent?: number;
  // Does opendoor own this property?
  isOpendoorOwned?: boolean;
  // Is the $1k ODHL closing credit available for this property?
  odhlCreditUnavailable?: boolean;
};

export type CostEstimatorMonthlyExpenses = {
  // Principal and interests is null (not displayed on the UI) when user is paying all cash
  principalAndInterests: number | null;
  interestRate: number | null;
  propertyTaxes: number;
  homeOwnersInsurance: number;
  hoa: number;
  total: number;
};

export type CostEstimatorDueAtClose = {
  // Down payment values are null (not displayed on the UI) when user is paying all cash
  downPaymentAmount: number | null;
  downPaymentPercentage: number | null;

  // Purchase price is null (not displayed on the UI) when user is paying via mortgage
  purchasePrice: number | null;

  closingCosts: number;
  estimatedClosingCredit: number;
  total: number;
};

export type CostEstimatorClosingCredit = {
  total: number;
};

export type CostEstimatorResults = {
  monthlyExpenses: CostEstimatorMonthlyExpenses;
  dueAtClose: CostEstimatorDueAtClose;
  closingCredit: CostEstimatorClosingCredit;
};

/**
 * Calculate closing costs
 */
export const calculateClosingCosts = (
  purchasePrice: number,
  closingCostRate = DEFAULT_CLOSING_COSTS_RATE,
  isUserPayingAllCash = false,
) => {
  return isUserPayingAllCash
    ? Math.floor(purchasePrice * CLOSING_COSTS_CASH_RATE)
    : Math.floor(purchasePrice * closingCostRate);
};

/**
 * Helper function to calculate the monthly expenses for a property
 */
export const calculateEstimatedCosts = (
  inputParameters: CostEstimatorInputParameters,
  propertyParameters: CostEstimatorPropertyParameters,
): CostEstimatorResults => {
  const userIsPayingAllCash = !(
    inputParameters.downPaymentAmount || inputParameters.downPaymentPercentage
  );
  // The next 3 values can be null if the user is paying all cash
  // The UI handles the null values
  let monthlyMortgagePayment: number | null = null;
  let downPaymentCalculatedAmount: number | null = null;
  let downPaymentCalculatedPercentage: number | null = null;

  const monthlyInsurance = Math.floor(
    (inputParameters.purchasePrice * propertyParameters.homeOwnersInsuranceRate) / 1200,
  );
  const monthlyPropertyTax = Math.floor(
    (inputParameters.purchasePrice * propertyParameters.propertyTaxRate) / 12,
  );
  const estimatedClosingCosts = calculateClosingCosts(
    inputParameters.purchasePrice,
    propertyParameters.closingCostsRate,
    userIsPayingAllCash,
  );

  if (!userIsPayingAllCash) {
    // Calculating down payment amount if not given as parameter
    downPaymentCalculatedAmount = inputParameters.downPaymentAmount || 0;
    if (!downPaymentCalculatedAmount && inputParameters.downPaymentPercentage) {
      const baseDownPayment = Math.round(
        inputParameters.purchasePrice * (inputParameters.downPaymentPercentage / 100.0),
      );
      downPaymentCalculatedAmount = roundToAmount(baseDownPayment, LARGE_NUMBER_ROUND_TO);
    }

    // Calculating down payment percentage if not given as parameter
    downPaymentCalculatedPercentage = inputParameters.downPaymentPercentage || 0;
    if (!downPaymentCalculatedPercentage) {
      downPaymentCalculatedPercentage = Math.round(
        (downPaymentCalculatedAmount / inputParameters.purchasePrice) * 100,
      );

      if (downPaymentCalculatedPercentage > 90) {
        // Limiting the down payment percentage to be 90
        // Recalculating total
        downPaymentCalculatedPercentage = 90;
        downPaymentCalculatedAmount = Math.round(
          inputParameters.purchasePrice * (downPaymentCalculatedPercentage / 100.0),
        );
      }
    }

    monthlyMortgagePayment = calculateMonthlyMortgagePayment(
      inputParameters.interestRate,
      getMortgagePeriodYears(inputParameters.mortgagePeriod),
      downPaymentCalculatedAmount,
      inputParameters.purchasePrice,
    );
  }

  const closingCredit: CostEstimatorClosingCredit = {
    total: 0,
  };

  const principalAndInterests = userIsPayingAllCash ? null : monthlyMortgagePayment;
  const monthlyExpenses: CostEstimatorMonthlyExpenses = {
    principalAndInterests,
    interestRate: userIsPayingAllCash ? null : inputParameters.interestRate,
    propertyTaxes: monthlyPropertyTax,
    homeOwnersInsurance: monthlyInsurance,
    hoa: propertyParameters.monthlyHoaFee,
    total:
      (principalAndInterests || 0) +
      monthlyPropertyTax +
      monthlyInsurance +
      propertyParameters.monthlyHoaFee,
  };

  const dueAtClose: CostEstimatorDueAtClose = {
    downPaymentAmount: downPaymentCalculatedAmount,
    downPaymentPercentage: downPaymentCalculatedPercentage,
    purchasePrice: userIsPayingAllCash ? inputParameters.purchasePrice : null,
    closingCosts: estimatedClosingCosts,
    estimatedClosingCredit: closingCredit.total,
    total:
      estimatedClosingCosts +
      (downPaymentCalculatedAmount || 0) +
      (userIsPayingAllCash ? inputParameters.purchasePrice : 0) -
      closingCredit.total,
  };

  return {
    monthlyExpenses,
    dueAtClose,
    closingCredit,
  };
};

/**
 * Helper function to round slider values
 * @param {Number} val - The value you want to round
 * @param {Number} roundTo - The smallest unit you want to round to
 * @returns {Number}
 */
export const roundToAmount = (val: number, roundTo: number): number => val - (val % roundTo);

/**
 *
 * Helper function to calculate mortgage payments
 *
 * https://en.wikipedia.org/wiki/Mortgage_calculator
 */
const calculateMonthlyMortgagePayment = (
  interestRate: number,
  mortgagePeriod: number,
  downPaymentAmount: number,
  purchasePrice: number,
) => {
  const monthlyInterestRate = interestRate / 100.0 / NUMBER_OF_MONTHS;
  const numberOfPayments = mortgagePeriod * NUMBER_OF_MONTHS;
  const principal = purchasePrice - downPaymentAmount;

  if (monthlyInterestRate > 0) {
    return Math.round(
      (monthlyInterestRate * principal) / (1 - (1 + monthlyInterestRate) ** -numberOfPayments),
    );
  }
  return Math.round(principal / numberOfPayments);
};

/**
 * Helper function to get the mortgage period in number of years
 */
export const getMortgagePeriodYears = (mortgagePeriod: MortgagePeriod): number => {
  switch (mortgagePeriod) {
    case MortgagePeriod.ThirtyYearFixed:
      return 30;
    case MortgagePeriod.FifteenYearFixed:
      return 15;
    case MortgagePeriod.SevenOneArm:
    case MortgagePeriod.FiveOneArm:
    default:
      return 30;
  }
};
